import { browserHistory } from 'react-router';
import { delay, call, put, takeEvery, takeLatest, select } from 'redux-saga/effects';
import { arrayRemove } from 'redux-form';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';

import * as apiCustomField from '../../services/custom_field/api_custom_field';
import * as apiCommon from '../../services/apiCommon';
import * as common from '../sagasCommon';
import { CUSTOM_FIELD, ENTITY } from '../../constants/constant_doc_types';
import { CustomFieldForm } from '../../constants/constant_form_names';
import { getStdFieldKey } from '../../services/apiUtils';
import { STD_FIELDS_ENTITY } from '../../constants/constant_standard_fields';
import { getInitialFormValues } from '../../common/selectors/selector_common';

export function* fetchListCustomFieldSaga(app) {
  yield takeLatest('FETCH_LIST_CUSTOM_FIELD', callFetchListCustomField, app);
}

function* callFetchListCustomField(app, action) {
  const { filters, silent } = action;

  try {
    yield common.showProgressListTable(silent);

    const dataParams = {
      filters,
      list_type: CUSTOM_FIELD,
    };
    const tableData = yield call(apiCommon.getDocs, app, dataParams);

    yield call(common.doneListTable, {
      rows: tableData.data,
      total_rows: tableData.total,
    });
  } catch (error) {
    yield common.showErrorListTable(error);
  }
}

export function* fetchCustomFieldForEditSaga(app) {
  yield takeEvery(
    'FETCH_CUSTOM_FIELD_FOR_EDIT',
    callFetchCustomFieldForEdit,
    app,
  );
}

function* callFetchCustomFieldForEdit(app, action) {
  const { id, silent } = action;

  try {
    if (id) {
      yield common.showProgressDataById(silent);

      const params = { id };
      const doc = yield call(apiCustomField.fetchCustomFieldById, app, params);

      yield common.doneDataById(doc);
    }
  } catch (error) {
    yield put({ type: 'FETCH_DATA_BY_ID_ERROR', error: error });
  }
}

export function* saveCustomFieldSaga(app) {
  yield takeLatest('SAVE_CUSTOM_FIELD', callSaveCustomField, app);
}

function* callSaveCustomField(app, action) {
  const { tenant, user } = yield select();
  const { data } = action;

  try {
    yield put({ type: 'SAVE_RECORD_INPROGRESS' });

    const params = { data, tenant, user };
    const result = yield call(apiCustomField.saveCustomField, app, params);

    yield call(callFetchCustomFieldForEdit, app, {
      id: result.id,
      silent: true,
    });

    browserHistory.push(`/admin/custom/field/edit/${result.id}`);

    yield common.doneSaveRecord(result, 'Custom Field was saved');
  } catch (e) {
    yield common.showErrorSaveRecord(e);
  }
}

export function* deleteCustomFieldSaga(app) {
  yield takeEvery('DELETE_CUSTOM_FIELD', callDeleteCustomField, app);
}

function* callDeleteCustomField(app, action) {
  const { tenant, user } = yield select();
  const { id } = action;

  try {
    yield put({ type: 'DELETE_RECORD_INPROGRESS' });

    const params = { tenant, user, id };

    yield call(apiCustomField.deleteCustomField, app, params);
    yield put({
      type: 'DELETE_RECORD_DONE',
      payload: { id, docType: CUSTOM_FIELD },
    });
    yield delay(2000);

    browserHistory.push('/admin/custom/field');
  } catch (error) {
    yield put({ type: 'DELETE_RECORD_ERROR', error });
  }
}

export function* getCustomFieldTabSettingLinesSaga() {
  yield takeEvery(
    'GET_CUSTOM_FIELD_TAB_SETTING_LINES',
    callGetCustomFieldTabSettingLines,
  );
}

export function* callGetCustomFieldTabSettingLines(action) {
  try {
    const {
      payload: { removedAppliedTo, tabSettings },
    } = action;

    for (const docType of removedAppliedTo) {
      const index = tabSettings.findIndex(
        tabSetting => tabSetting.assigned_doc_type === docType,
      );

      if (index >= 0) {
        yield put(arrayRemove(CustomFieldForm, 'tabs', index));
      }
    }
  } catch (e) {
    yield common.showErrorSaveRecord(e);
  }
}

export function* overrideFormDataWithCustomForm(docType, formName, data) {
  const { custom_fields } = data;

  if (isEmpty(custom_fields)) {
    return data;
  }

  const oldData = yield select(getInitialFormValues, formName);
  const newData = { ...data };

  if (docType === ENTITY) {
    for (const fieldName of STD_FIELDS_ENTITY) {
      const value = custom_fields[getStdFieldKey(fieldName)];

      if (isEmpty(value)) {
        continue;
      }

      switch (fieldName) {
        case 'date_of_birth':
        case 'email':
        case 'first_name':
        case 'gender':
        case 'last_name':
          if (oldData[fieldName] !== newData[fieldName]) {
            break;
          }
          
          if (fieldName === 'date_of_birth') {
            set(newData, fieldName, moment(value).format('MM/DD/YYYY'));
            break;
          }
          
          set(newData, fieldName, value);
          break;
        case 'preferred_language':
          const changed = oldData['preferred_language_id'] !== newData['preferred_language_id'];

          if (changed) {
            set(newData, `custom_fields.${getStdFieldKey(fieldName)}`, newData['preferred_language_id']);
            break;
          }

          set(newData, 'preferred_language_id', value);
          break;
        case 'address1':
        case 'address2':
        case 'city':
        case 'country':
        case 'zipcode': {
          const addressDetailsIsEmpty = isEmpty(newData.address_details);
          const oldAddressDetails = !addressDetailsIsEmpty
            ? oldData.address_details
            : [{}];
          const newAddressDetails = !addressDetailsIsEmpty
            ? newData.address_details
            : [{}];
          const index = 0;
          const oldAddressDetail = oldAddressDetails[index];
          const newAddressDetail = newAddressDetails[index];

          const key = fieldName === 'country' ? 'country_id' : fieldName;

          if (
            (newAddressDetail.id && oldAddressDetail[key] !== newAddressDetail[key])
            || newAddressDetails.length < oldAddressDetails.length
          ) {
            continue;
          }

          newAddressDetail[key] = value;

          set(newData, `address_details[${index}]`, newAddressDetail);

          break;
        }
        case 'contact_number': {
          const contactNumbersIsEmpty = isEmpty(newData.contact_numbers);
          const oldContactNumbers = !contactNumbersIsEmpty
            ? oldData.contact_numbers
            : [{}];
          const newContactNumbers = !contactNumbersIsEmpty
            ? newData.contact_numbers
            : [{}];
          const oldContactNumberIndex = oldContactNumbers.length - 1;
          const newContactNumberIndex = newContactNumbers.length - 1;
          const oldContactNumber = oldContactNumbers[oldContactNumberIndex];
          const newContactNumber = newContactNumbers[newContactNumberIndex];

          if (oldContactNumber.value !== newContactNumber.value) {
            break;
          }

          newContactNumber.value = value;

          set(newData, `contact_numbers[${newContactNumberIndex}]`, newContactNumber);

          break;
        }
        default:
          break;
      }
    }
  }

  return newData;
}